import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/AuthProvider';
import { DataService, Organization } from './services/DataService';
import { 
  ArrowLeft,
  Key,
  Copy,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Shield,
  Globe,
  Code,
  BookOpen
} from 'lucide-react';

interface ApiAccessManagementProps {
  organizationId: string;
  onBack: () => void;
}

const ApiAccessManagement: React.FC<ApiAccessManagementProps> = ({
  organizationId,
  onBack
}) => {
  const { currentUser } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Token management state
  const [generatedToken, setGeneratedToken] = useState<string>('');
  const [tokenCountdown, setTokenCountdown] = useState<number>(0);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [tokenGenerating, setTokenGenerating] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);

  useEffect(() => {
    loadOrganizationData();
  }, [organizationId]);

  // Auto-dismiss messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Token countdown timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (tokenCountdown > 0) {
      timer = setTimeout(() => {
        setTokenCountdown(prev => {
          if (prev <= 1) {
            setGeneratedToken('');
            setTokenCopied(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [tokenCountdown]);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      const org = await DataService.getOrganization(organizationId);
      setOrganization(org);
    } catch (err: any) {
      console.error('Error loading organization data:', err);
      setError(err.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateApiToken = async () => {
    setTokenGenerating(true);
    try {
      const result = await DataService.createApiToken(organizationId);
      setGeneratedToken(result.data.token);
      setTokenCountdown(30); // 30 seconds to view token
      setTokenCopied(false);
      setSuccess(result.message || 'API token generated successfully');
    } catch (err: any) {
      console.error('Error generating API token:', err);
      setError(err.message || 'Failed to generate API token');
    } finally {
      setTokenGenerating(false);
    }
  };

  const handleCopyToken = async () => {
    if (generatedToken) {
      try {
        await navigator.clipboard.writeText(generatedToken);
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
      } catch (err) {
        setError('Failed to copy token to clipboard');
      }
    }
  };

  const formatOrganizationName = (name: string): string => {
    return name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading API Access...</h2>
          <p className="text-gray-600">Please wait while we load your API access information</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Organization not found
          </div>
          <button 
            onClick={onBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Back to organization"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Key className="w-6 h-6 text-purple-600 mr-3" />
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">API Access</h1>
                  <p className="text-sm text-gray-500">{formatOrganizationName(organization.name)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                API User
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button 
                onClick={() => setError('')}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-green-50 border-l-4 border-green-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
              <button 
                onClick={() => setSuccess('')}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* API Token Management */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Your API Token</h2>
              <p className="text-sm text-gray-600 mt-1">
                Generate or refresh your API token to access {formatOrganizationName(organization.name)} data programmatically
              </p>
            </div>
            <button
              onClick={() => setShowTokenModal(true)}
              disabled={tokenGenerating}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${tokenGenerating ? 'animate-spin' : ''}`} />
              {tokenGenerating ? 'Generating...' : 'Generate Token'}
            </button>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start">
              <Key className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-purple-900">API Token Information</h4>
                <div className="text-sm text-purple-700 mt-1 space-y-1">
                  <p>• Each user can have only one API token per organization</p>
                  <p>• Generating a new token will replace your existing token</p>
                  <p>• Tokens are only visible for 30 seconds after generation</p>
                  <p>• Save your token securely - you won't be able to see it again</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Documentation */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex items-center mb-6">
            <BookOpen className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">API Documentation</h2>
          </div>

          <div className="space-y-6">
            {/* Base URL */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Base URL</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <code className="text-sm text-gray-800">
                  https://europe-west2-{'{project-id}'}.cloudfunctions.net/
                </code>
              </div>
            </div>

            {/* Authentication */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Authentication</h3>
              <p className="text-sm text-gray-600 mb-3">
                All API requests must include your API token in the Authorization header:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <code className="text-sm text-gray-800">
                  Authorization: Bearer YOUR_API_TOKEN
                </code>
              </div>
            </div>

            {/* Available Endpoints */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Available Endpoints</h3>
              <div className="space-y-4">
                {/* Get Document */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 mr-3">
                      GET
                    </span>
                    <code className="text-sm font-mono text-gray-800">getDocument</code>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Retrieve a single document from a collection</p>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <code className="text-xs text-gray-800">
                      GET /getDocument?org={organizationId}&collection=&#123;collectionId&#125;&document=&#123;documentId&#125;
                    </code>
                  </div>
                </div>

                {/* Get Collection */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 mr-3">
                      GET
                    </span>
                    <code className="text-sm font-mono text-gray-800">getCollection</code>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Retrieve all documents from a collection</p>
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <code className="text-xs text-gray-800">
                      GET /getCollection?org={organizationId}&collection=&#123;collectionId&#125;
                    </code>
                  </div>
                </div>
              </div>
            </div>

            {/* Example Usage */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Example Usage</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <pre className="text-xs text-gray-800 overflow-x-auto">
{`curl -H "Authorization: Bearer YOUR_API_TOKEN" \\
     "https://europe-west2-project.cloudfunctions.net/getDocument?org=${organizationId}&collection=users&document=user123"`}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Collections */}
        {organization.collections && organization.collections.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center mb-6">
              <Globe className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Available Collections</h2>
            </div>

            <div className="grid gap-4">
              {organization.collections.map(collection => (
                <div key={collection.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{collection.name}</h3>
                      <p className="text-sm text-gray-500">Collection ID: <code className="bg-gray-100 px-1 rounded">{collection.id}</code></p>
                      {collection.schema && (
                        <p className="text-xs text-gray-400 mt-1">
                          {Object.keys(collection.schema.fields).length} fields defined
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {collection.schema && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Schema Available
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* API Endpoints for this collection */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-700 mb-2">API Endpoints:</p>
                    <div className="space-y-1">
                      <code className="text-xs text-gray-600 block">
                        GET /getCollection?org={organizationId}&collection={collection.id}
                      </code>
                      <code className="text-xs text-gray-600 block">
                        GET /getDocument?org={organizationId}&collection={collection.id}&document=&#123;docId&#125;
                      </code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Token Generation Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Key className="w-6 h-6 text-purple-600 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">Generate API Token</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowTokenModal(false);
                    setGeneratedToken('');
                    setTokenCountdown(0);
                    setTokenCopied(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="px-6 py-6">
              {!generatedToken ? (
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-purple-900">Important Security Notice</h4>
                        <p className="text-sm text-purple-700 mt-1">
                          Your API token will only be visible for 30 seconds after generation. 
                          Make sure to copy and save it somewhere secure immediately.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <p>This will generate a new API token for your account to access {formatOrganizationName(organization.name)} data via API endpoints.</p>
                    <p className="mt-2">If you already have a token, generating a new one will replace it.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-900">Token Generated</span>
                      </div>
                      <div className="flex items-center text-orange-600">
                        <Clock className="w-4 h-4 mr-1" />
                        <span className="text-sm font-medium">{tokenCountdown}s remaining</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Your API Token
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={generatedToken}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                          />
                          <button
                            onClick={handleCopyToken}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                              tokenCopied
                                ? 'bg-green-100 text-green-800 border border-green-300'
                                : 'bg-purple-100 text-purple-800 border border-purple-300 hover:bg-purple-200'
                            }`}
                          >
                            {tokenCopied ? (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1 inline" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-1 inline" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-orange-50 border border-orange-200 rounded p-3">
                        <p className="text-xs text-orange-800">
                          <strong>Save this token now!</strong> You won't be able to see it again after {tokenCountdown} seconds. 
                          Use it as a Bearer token in your API requests: <code className="bg-orange-100 px-1 rounded">Authorization: Bearer {generatedToken.substring(0, 20)}...</code>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              {!generatedToken ? (
                <>
                  <button
                    onClick={() => setShowTokenModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateApiToken}
                    disabled={tokenGenerating}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {tokenGenerating ? 'Generating...' : 'Generate Token'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowTokenModal(false);
                    setGeneratedToken('');
                    setTokenCountdown(0);
                    setTokenCopied(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiAccessManagement;